import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Filter, FilterGroup, SearchObject } from '../redux/types/types';
import { popState } from './useSyncUrlOnPop';

/**
 * useSearchFlow — state + URL orchestration for the search form.
 *
 * Responsibilities
 * - Holds an editable draft of the SearchObject (with draftRef to avoid async setState races).
 * - Ingests server FilterGroup[] and normalizes to a flat Filter[] using composite ids "group::facet"
 *   to support OR-within-group and AND-across-groups.
 * - Exposes setters: setField, setFilterSelected(group, key, selected), setSelectedFilterKeys(keys),
 *   setFiltersFromGroups(groups), replaceDraft(next), and submit(keepPage?).
 * - submit(keepPage=false): commit draft, optionally preserve page number, build query string, push a new URL,
 *   and store selected composite ids in history.state for POP/restore; also calls onCommit(committed)
 *   so the parent can execute the actual fetch.
 * - Reacts to incoming FilterGroup[] (e.g., after a fetch) and preserves prior selections.
 *
 * Notes
 * - URL building/parsing is handled by buildQueryFromSearchObject / parseQueryToSearchObject.
 * - Non-URL fields (e.g., filters payload, typeAheadValues) are excluded from the query string.
 */
export function useSearchFlow(
  initial: SearchObject,
  empty: SearchObject,
  filters: FilterGroup[],
  onCommit?: (obj: any) => void,
) {
  const location = useLocation();
  const navigate = useNavigate();

  const [draft, setDraft] = useState<SearchObject>(initial);

  const emptySerachObjectRef = useRef<SearchObject>(empty);
  const draftRef = useRef<SearchObject>(draft);

  // Keep draftRef always current with the active draft
  // The ref is used in places where the state may or may not have been updated yet due to
  // setting a new value and edning up in a react batch update.
  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    if (filters && filters.length) {
      setFiltersFromGroups(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Maps a facet key to its owning FilterGroup field (e.g., "sv" → "sprak") for lookups/restoring selection.
  const facetKeyToGroupRef = useRef<Record<string, string>>({});

  // Filter duplicate filters by key
  const distinctByKey = (filters: Filter[]) => {
    const currentFilterKeys = new Set<string>();
    return filters.filter(
      (f) =>
        f &&
        f.key &&
        !currentFilterKeys.has(f.key) &&
        currentFilterKeys.add(f.key),
    );
  };

  // Mark selected filters with the selected value
  const withSelection = (filters: Filter[], keys: string[]) => {
    const selectedKeys = new Set(keys ?? []);
    return (filters ?? []).map((f) => ({
      ...f,
      selected: selectedKeys.has(f.key),
    }));
  };

  // Map filter groups and flatten them for inclusion in searchObject
  const setFiltersFromGroups = useCallback((groups: FilterGroup[]) => {
    const prevSelectedIds = (draftRef.current.filters ?? [])
      .filter((f) => f.selected)
      .map((f) => f.key);

    const flat: Filter[] = [];
    for (const g of groups ?? []) {
      for (const f of g.facets ?? []) {
        const id = `${g.fieldName}::${f.key}`;
        flat.push({
          key: id,
          description: f.description,
          count: f.count,
          selected: false, // temp, this is set below
        });
      }
    }

    // Set selected on selected filters
    const filtersWithSelected = withSelection(flat, prevSelectedIds);
    setDraft((prev) => ({ ...prev, filters: filtersWithSelected }));
    draftRef.current = { ...draftRef.current, filters: filtersWithSelected };
  }, []);

  // Replace the flat filter list while preserving selection:
  // - De-duplicate incoming filters by key
  // - Mark selected flags based on the current draft selection
  // - Update state and sync draftRef
  const setFilters = useCallback((filters: Filter[]) => {
    const currentSelected = (draftRef.current.filters ?? [])
      .filter((f) => f.selected)
      .map((f) => f.key);
    const filtersWithSelected = withSelection(
      distinctByKey(filters ?? []),
      currentSelected,
    );
    setDraft((prev) => ({ ...prev, filters: filtersWithSelected }));
    draftRef.current = { ...draftRef.current, filters: filtersWithSelected };
  }, []);

  // Set single filter option as Selected
  const setFilterSelected = useCallback(
    (group: string, key: string, selected: boolean) => {
      const compositeKey = `${group}::${key}`;
      setDraft((prev) => {
        const nextFilters = (prev.filters ?? []).map((f) =>
          f.key === compositeKey ? { ...f, selected } : f,
        );
        const next = { ...prev, filters: nextFilters } as SearchObject;
        draftRef.current = next;
        return next;
      });
    },
    [],
  );

  // Replace ALL selections in one shot.
  // `keys` are composite ids ("group::key"); empty array clears all selections.
  const setSelectedFilterKeys = useCallback((keys: string[]) => {
    const next = keys?.length
      ? withSelection(draftRef.current.filters ?? [], keys ?? [])
      : withSelection([], []);

    setDraft((prev) => ({ ...prev, filters: next }));
    draftRef.current = { ...draftRef.current, filters: next };
  }, []);

  // Set single field value and update draft
  const setField = useCallback((svKey: string, value: any) => {
    const mk = mapQueryKeyToModelKey(svKey);

    const next = {
      ...draftRef.current,
      [mk]:
        mk === 'exactSearch'
          ? !!value
          : mk === 'page'
            ? Number(value) || 0
            : mk === 'pageSize'
              ? Number(value) || 0
              : value,
    };
    draftRef.current = next;
    setDraft(next);
    return next;
  }, []);

  // Replace entire draft with init values and supplied values of next
  const replaceDraft = useCallback((next: Record<string, any>) => {
    const merged = { ...emptySerachObjectRef.current, ...next } as SearchObject;
    setDraft(merged);
    draftRef.current = merged;
  }, []);

  // Commit the current draft and push a new URL.
  // - If keepPage=true, preserve current page; otherwise reset to page 0.
  // - Build query string from the committed state.
  // - Call onCommit(committed) so the parent updates searchObject.
  // - Save selected facets as "group::key" in history.state for POP/restore.
  // - Navigate only when the URL actually changes (creates a history entry).
  const submit = useCallback(
    (opts?: { keepPage?: boolean; skipNavigate?: boolean }) => {
      const committed: SearchObject = {
        ...draftRef.current,
        page: opts?.keepPage ? draftRef.current.page : 0,
      };

      setDraft(committed);

      const qs = buildQueryFromSearchObject(committed);
      onCommit && onCommit(committed);

      const selectedIds = (committed.filters ?? [])
        .filter((f) => f.selected)
        .map((f) => {
          const g = facetKeyToGroupRef.current[f.key];
          return g ? `${g}::${f.key}` : f.key;
        });

      // Detect filter selection changes and incorporate these into history
      const prevSelected = (location.state as any)?.selectedFilters as
        | string[]
        | undefined;
      const stateChanged = !equalAsSet(prevSelected ?? [], selectedIds);

      // Navigate if we are not in a state of POP-navigation
      if (
        !(popState.isInPopState || opts?.skipNavigate) &&
        (`?${qs}` !== location.search || stateChanged)
      ) {
        navigate(
          { pathname: location.pathname, search: qs ? `?${qs}` : '' },
          { state: { selectedFilters: selectedIds } },
        );
      }
    },
    [onCommit, navigate, location.search, location.pathname],
  );

  return {
    draft,
    replaceDraft,
    setField,
    setFilters,
    setFiltersFromGroups,
    setFilterSelected,
    setSelectedFilterKeys,
    submit,
  };
}

// Determine equality of two string arrays
const equalAsSet = (a: string[] = [], b: string[] = []) => {
  if (a.length !== b.length) return false;
  const aAsSet = new Set(a);
  for (const x of b) if (!aAsSet.has(x)) return false;
  return true;
};

const toBool = (value: any) =>
  typeof value === 'boolean'
    ? value
    : typeof value === 'string'
      ? /^(true|1|yes|on)$/i.test(value)
      : !!value;

// Parse location.search into a partial SearchObject:
// - Translate Swedish query params to model keys (e.g. "titel"→"title", "sprak"→"language")
// - page: URL is 1-based → model is 0-based (number)
// - exactSearch → boolean
// - pageSize/sortBy → numbers
// - All other fields remain strings (already URL-decoded)
export function parseQueryToSearchObject(search: string) {
  const BOOL_MODEL_KEYS = new Set(['exactSearch']);
  const searchParams = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search,
  );
  const out: any = {};

  searchParams.forEach((value, key) => {
    const modelKey = mapQueryKeyToModelKey(key);
    if (modelKey === 'page') {
      const n = parseInt(value, 10);
      out.page = Number.isFinite(n) && n > 0 ? n - 1 : 0;
      return;
    }
    if (BOOL_MODEL_KEYS.has(modelKey)) {
      out[modelKey] = toBool(value);
      return;
    }
    out[modelKey] = value;
  });

  return out;
}

// Build a query string from a SearchObject model:
// - Translate model keys → Swedish query keys (e.g. "title"→"titel").
// - Exclude non-URL fields: excludeDownloadable, filters, typeAheadValues.
// - page: 0-based model → 1-based "sida" (only if finite).
// - exactSearch: include only when true → "exact=true".
// - Ignore arrays/objects and null/undefined values.
// - Trim and skip empty strings.
// Returns a URLSearchParams string (e.g. "titel=Foo&sida=2").
export function buildQueryFromSearchObject(obj: Record<string, any>): string {
  const sp = new URLSearchParams();
  const EXCLUDE_FROM_QUERY_MODEL_KEYS = new Set([
    'excludeDownloadable',
    'filters',
    'typeAheadValues',
  ]);

  for (const [modelKey, raw] of Object.entries(obj ?? {})) {
    if (raw == null) continue;
    if (EXCLUDE_FROM_QUERY_MODEL_KEYS.has(modelKey)) continue;

    if (modelKey === 'page') {
      const n = Number(raw);
      if (Number.isFinite(n))
        sp.set('sida', String(Math.max(1, Math.floor(n) + 1)));
      continue;
    }

    if (modelKey === 'exactSearch') {
      raw && sp.set('exact', raw);
      continue;
    }

    if (Array.isArray(raw) || typeof raw === 'object') {
      continue;
    }

    const qKey = modelKeyToQueryKey[modelKey] ?? modelKey;
    const val = String(raw).trim();
    if (val !== '') sp.set(qKey, val);
  }

  return sp.toString();
}

const queryKeyToModelKey: Record<string, string> = {
  titel: 'title',
  medarbetare: 'coworker',
  serie: 'series',
  amne: 'subject',
  varugrupp: 'commodityGroup',
  marknadsserie: 'marketSeries',
  forlag: 'publisher',
  sprak: 'language',
  exact: 'exactSearch',
  sida: 'page',
  query: 'searchString',
  fran: 'fromReleaseDate',
  till: 'toReleaseDate',
};

const modelKeyToQueryKey = Object.fromEntries(
  Object.entries(queryKeyToModelKey).map(([sv, en]) => [en, sv]),
) as Record<string, string>;

export const mapQueryKeyToModelKey = (k: string) => queryKeyToModelKey[k] ?? k;
